// Fase 15A — Dettaglio azienda lato platform: onboarding checklist (65),
// attivazione (66-67), sospensione (48), transfer consulente (25), assegnazioni
// e moduli effettivi. Solo metadata amministrativi (75).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Leaf, CheckCircle2, XCircle, ArrowLeftRight, CreditCard, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function PlatformCompanyDetail() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [features, setFeatures] = useState(null);
  const [plans, setPlans] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [transfer, setTransfer] = useState({ new_consultant_user_id: "", reason: "" });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, o, a, f, cons, pl] = await Promise.all([
        api.get(`/admin/platform/companies/${companyId}`),
        api.get(`/admin/platform/companies/${companyId}/onboarding`),
        api.get("/admin/platform/assignments", { params: { company_id: companyId } }),
        api.get("/admin/platform/features", { params: { company_id: companyId } }),
        api.get("/admin/platform/consultants"),
        api.get("/admin/platform/plans"),
      ]);
      setCompany(c.data);
      setPlans(pl.data.plans || []);
      setOnboarding(o.data);
      setAssignments(a.data.items || []);
      setFeatures(f.data);
      setConsultants(cons.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api e setState sono stabili
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // FASE 2/5: contesto azienda esplicito, visibile e tracciato — ingresso auditato
  // al mount, banner persistente, uscita auditata all'abbandono della pagina.
  const [ctx, setCtx] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    api.post(`/admin/platform/companies/${companyId}/context/enter`, {})
      .then((r) => { if (alive) setCtx(r.data); })
      .catch(() => {});
    return () => {
      alive = false;
      api.post(`/admin/platform/companies/${companyId}/context/exit`).catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al mount/unmount
  }, [companyId]);

  const exitContext = async () => {
    try { await api.post(`/admin/platform/companies/${companyId}/context/exit`); } catch { /* exit già registrato all'unmount */ }
    navigate("/admin/platform/aziende");
  };

  const contextBanner = ctx && (
    <div className="sticky top-16 z-30 -mx-1 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 flex flex-wrap items-center gap-3"
      role="status" data-testid="superadmin-context-banner">
      <p className="text-xs font-semibold text-amber-900 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 shrink-0" aria-hidden="true" />
        Stai operando come Superadmin nel contesto di: <strong>{ctx.ragione_sociale}</strong>
        <span className="font-normal text-amber-700">· ingresso {new Date(ctx.entered_at).toLocaleString("it-IT")} · accesso tracciato in audit</span>
      </p>
      <Button size="sm" variant="outline" className="h-7 text-xs ml-auto border-amber-400 text-amber-900"
        onClick={exitContext} data-testid="exit-company-context">
        Esci dal contesto azienda
      </Button>
    </div>
  );

  const activate = async () => {
    try {
      await api.post(`/admin/platform/companies/${companyId}/activate`);
      toast.success("Azienda attivata");
      await load();
    } catch (e) {
      const missing = e.response?.data?.detail?.missing;
      toast.error("Attivazione bloccata", {
        description: missing ? `Manca: ${missing.join(", ")}` : formatApiError(e) });
    }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/admin/platform/companies/${companyId}/status`, null,
        { params: { status } });
      toast.success(`Stato azienda → ${status}`);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const doTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/platform/companies/${companyId}/transfer`, transfer);
      toast.success("Azienda trasferita", {
        description: "Lo storico assegnazioni è preservato." });
      setTransfer({ new_consultant_user_id: "", reason: "" });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const toggleModule = async (module, enabled) => {
    try {
      await api.put("/admin/platform/features", {
        level: "COMPANY", module, enabled,
        company_id: companyId, organization_id: company?.organization_id });
      toast.success(`${module} → ${enabled ? "ATTIVO" : "DISATTIVO"}`);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const setPlan = async (planId) => {
    try {
      await api.put(`/admin/platform/companies/${companyId}/plan`,
        { plan_id: planId === "NONE" ? null : planId });
      toast.success("Piano aggiornato");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="company-detail-error">{error}</p>;
  if (!company || !onboarding) return <p className="text-sm text-slate-600" data-testid="company-detail-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-company-detail">
      {contextBanner}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Leaf className="w-7 h-7 text-green-800" aria-hidden="true" />
            {company.denominazione}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {company.partita_iva || "—"} · {company.citta || company.nazione || "—"} ·
            codice cliente {company.customer_code || "—"}
          </p>
        </div>
        <Badge variant="outline" className="text-sm" data-testid="company-status-badge">
          {company.status || "ACTIVE"}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="onboarding-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Onboarding</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {onboarding.checks.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm"
                data-testid={`onboarding-${c.key}`}>
                {c.ok
                  ? <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" />
                  : <XCircle className="w-4 h-4 text-red-500" aria-hidden="true" />}
                <span className={c.ok ? "text-slate-700" : "text-red-700"}>{c.label}</span>
              </div>
            ))}
            <div className="pt-2 flex flex-wrap gap-2">
              <Button onClick={activate} disabled={!onboarding.ready || company.status === "ACTIVE"}
                className="bg-green-700 hover:bg-green-800" data-testid="company-activate-button">
                Attiva azienda
              </Button>
              {company.status !== "SUSPENDED" && company.status !== "ARCHIVED" && (
                <Button variant="outline" onClick={() => setStatus("SUSPENDED")}
                  data-testid="company-suspend-button">Sospendi</Button>
              )}
              {company.status === "SUSPENDED" && (
                <Button variant="outline" onClick={() => setStatus("ACTIVE")}
                  data-testid="company-reactivate-button">Riattiva</Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="transfer-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" aria-hidden="true" /> Trasferisci a altro consulente
          </CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={doTransfer} className="space-y-3">
              <div className="space-y-1">
                <Label>Nuovo lead consultant</Label>
                <Select value={transfer.new_consultant_user_id}
                  onValueChange={(v) => setTransfer((t) => ({ ...t, new_consultant_user_id: v }))}>
                  <SelectTrigger data-testid="transfer-consultant-select">
                    <SelectValue placeholder="Seleziona consulente" /></SelectTrigger>
                  <SelectContent>
                    {consultants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name} · {c.email}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="transfer-reason">Motivazione *</Label>
                <Input id="transfer-reason" required minLength={3} value={transfer.reason}
                  data-testid="transfer-reason-input"
                  onChange={(e) => setTransfer((t) => ({ ...t, reason: e.target.value }))} />
              </div>
              <Button type="submit" variant="outline"
                disabled={!transfer.new_consultant_user_id || transfer.reason.length < 3}
                data-testid="transfer-submit">Trasferisci</Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="company-plan-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="w-4 h-4" aria-hidden="true" /> Piano subscription (placeholder)
        </CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Select value={company.plan_id || "NONE"} onValueChange={setPlan}>
            <SelectTrigger data-testid="company-plan-select">
              <SelectValue placeholder="Nessun piano" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">Nessun piano</SelectItem>
              {plans.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} · {p.price_label}</SelectItem>))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            Solo assegnazione interna: nessun pagamento o addebito viene attivato.
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="company-assignments-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Assegnazioni (storico preservato)</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {assignments.length === 0 && (
            <p className="text-xs text-slate-500">Nessuna assegnazione.</p>)}
          {assignments.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
              data-testid={`assignment-${a.id}`}>
              <Badge variant="outline" className="text-[9px]">{a.consultant_role}</Badge>
              <span className="font-medium text-slate-800">{a.consultant_name || a.consultant_email}</span>
              <Badge variant="outline" className={a.status === "ACTIVE"
                ? "bg-green-50 text-green-800 border-green-300 text-[9px]"
                : "bg-slate-100 text-slate-500 border-slate-300 text-[9px]"}>{a.status}</Badge>
              <span className="text-slate-400">
                dal {a.valid_from?.slice(0, 10)}{a.valid_to ? ` al ${a.valid_to.slice(0, 10)}` : ""}
              </span>
            </div>
          ))}
          <Link to="/admin/platform/assegnazioni"
            className="text-xs text-green-700 hover:underline"
            data-testid="goto-assignments">Gestisci assegnazioni →</Link>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="company-modules-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Moduli effettivi (GLOBAL → Studio → Azienda)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {features?.effective && Object.entries(features.effective).map(([m, on]) => (
            <button key={m} type="button" onClick={() => toggleModule(m, !on)}
              data-testid={`module-toggle-${m}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                on ? "bg-green-50 text-green-800 border-green-300 hover:bg-green-100"
                   : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200"}`}>
              {m} · {on ? "ON" : "OFF"}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
